// Full simulations table, drilled to from the All Simulations card's
// "View all" on the Simulations page. Every sim in scope with sends,
// click/report rates, difficulty and risk; sortable, paginated and
// exportable via the shared DataTable. Row click drills to the sim.
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { SIMS, SEND_LOG } from '../data';
import ActiveFilters from './ActiveFilters';
import DataTable from './DataTable';
import { tagsForSim, tagLabel } from '../tagAnalytics';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };

// Sims in scope = sims with at least one send event matching the scope
// (mirrors SimsPage's scopedSims), or the whole catalogue when unscoped.
function scopedSims(scope) {
    const s = scope || {};
    if (!Object.keys(s).length) return SIMS;
    const names = new Set(SEND_LOG.filter((e) => {
        if (!isIn(e.simName,  s.sim))        return false;
        if (!isIn(e.dept,     s.department)) return false;
        if (!isIn(e.location, s.location))   return false;
        if (!isIn(e.city,     s.city))       return false;
        if (!isIn(e.country,  s.country))    return false;
        if (!isIn(e.company,  s.company))    return false;
        if (!isIn(e.state,    s.state))      return false;
        if (!isIn(e.adGroups,  s.adGroup))    return false;
        if (s.user) {
            const vs = Array.isArray(s.user) ? s.user : [s.user];
            if (!vs.some((v) => e.email === v || `${e.firstName} ${e.lastName}` === v)) return false;
        }
        return true;
    }).map((e) => e.simName));
    return SIMS.filter((sim) => names.has(sim.name));
}

const LV_LABEL = { high: 'High', med: 'Medium', low: 'Low' };
const LV_COLOR = { high: '#FF5252', med: '#FFCA28', low: '#66BB6A' };

export default function AllSimsPage({ drill, scope, removeScope }) {
    const rows = useMemo(() => scopedSims(scope), [scope]);

    const columns = [
        { id: 'name',       label: 'Simulation',  always: true,    sortable: true, type: 'string', accessor: (s) => s.name,
          render: (s) => (
              <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>{s.name}</Typography>
                  {s.flag && <Typography sx={{ fontSize: 10, color: '#FF5252', fontWeight: 600 }}>! Flagged</Typography>}
              </Box>
          ) },
        { id: 'tags',       label: 'Lure Tags',   defaultOn: true, sortable: false, type: 'string', accessor: (s) => tagsForSim(s.name).map(tagLabel).join(', ') },
        { id: 'sent',       label: 'Sends',       defaultOn: true, sortable: true, type: 'number', accessor: (s) => s.sent || 0, align: 'right' },
        { id: 'click',      label: 'Click Rate',  defaultOn: true, sortable: true, type: 'number', accessor: (s) => parseInt(s.click) || 0, align: 'right',
          render: (s) => <Box component="span" sx={{ fontWeight: 700, color: LV_COLOR[s.lv] || 'text.primary' }}>{parseInt(s.click) || 0}%</Box> },
        { id: 'reportRate', label: 'Report Rate', defaultOn: true, sortable: true, type: 'number', accessor: (s) => s.reportRate ?? 0, align: 'right',
          format: (v) => `${v}%` },
        { id: 'diff',       label: 'Difficulty',  defaultOn: true, sortable: true, type: 'number', accessor: (s) => s.diff, align: 'right',
          format: (v) => `${v}/3` },
        { id: 'lv',         label: 'Risk',        defaultOn: true, sortable: true, type: 'string', accessor: (s) => s.lv,
          render: (s) => <Box component="span" sx={{ fontWeight: 600, color: LV_COLOR[s.lv] || 'text.secondary' }}>{LV_LABEL[s.lv] || 'Medium'}</Box> },
    ];

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <motion.div variants={fadeUp}>
                <DataTable
                    title="All Simulations"
                    columns={columns}
                    rows={rows}
                    rowKey={(s) => s.name}
                    onRowClick={(s) => drill('sim-detail', s.name, { type: 'sim', value: s.name })}
                    initialSort={{ by: 'click', dir: 'desc' }}
                    storageKey="rbx-all-sims-cols"
                    emptyMessage="No simulations in scope"
                />
            </motion.div>
        </motion.div>
    );
}
